import React, { useCallback, useEffect, useState } from 'react';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import { adminPaymentEvents, adminPaymentSummary, adminRefundNote, downloadInvoicePdf, fmtMoney, PaymentEvent, PaymentSummary } from '../../../api/billingApi';
import { apiRequest } from '../../../api/client';
import { S, fmtWhen } from './adminPageStyles';

/** Every payment on the platform (SRS #84): filter, open the invoice PDF, export CSV.
 *  Activity + Reconciliation tabs (SRS #83): the full gateway ledger — declines, webhooks (applied / ignored /
 *  unmatched / replayed), refunds, disputes — and the money reconciled against it. */

interface Row {
  id: number; invoice_number: string | null; pdf_available: boolean; gateway: string; gateway_transaction_id: string | null;
  amount: string; currency: string; status: string; description: string | null; invoice_url: string | null; created_at: string | null;
  email: string; organisation: string | null;
}

const opts = (): RequestInit => {
  const token = localStorage.getItem('policyora_token');
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const AdminInvoicesPage: React.FC = () => {
  const { sidebarCollapsed, setSidebarCollapsed, activeSection, setSelectedRegulation, handleNavClick, handleLogout } = useAdminNavigation();
  const [rows, setRows] = useState<Row[]>([]);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<'payments' | 'activity' | 'reconcile'>('payments');
  const [events, setEvents] = useState<PaymentEvent[]>([]);
  const [kinds, setKinds] = useState<Record<string, string>>({});
  const [kind, setKind] = useState('');
  const [outcome, setOutcome] = useState('');
  const [summary, setSummary] = useState<PaymentSummary | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [refunding, setRefunding] = useState<Row | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const qs = useCallback(() => {
    const q = new URLSearchParams();
    if (email) q.set('email', email);
    if (status) q.set('status', status);
    if (since) q.set('since', since);
    if (until) q.set('until', `${until}T23:59:59`);
    return q.toString();
  }, [email, status, since, until]);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    apiRequest<{ payments: Row[] }>(`/api/v1/admin/invoices?limit=500&${qs()}`, opts())
      .then((r) => setRows(r.payments)).catch((e) => setError(e instanceof Error ? e.message : 'Failed to load')).finally(() => setLoading(false));
  }, [qs]);
  useEffect(() => { load(); }, [load]);

  const loadEvents = useCallback(() => {
    const q = new URLSearchParams(qs());
    if (kind) q.set('kind', kind);
    if (outcome) q.set('outcome', outcome);
    setLoading(true); setError(null);
    adminPaymentEvents(q.toString()).then((r) => { setEvents(r.events); setKinds(r.kinds); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load')).finally(() => setLoading(false));
  }, [qs, kind, outcome]);
  const loadSummary = useCallback(() => {
    adminPaymentSummary(qs()).then(setSummary).catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, [qs]);
  useEffect(() => { if (tab === 'activity') loadEvents(); if (tab === 'reconcile') loadSummary(); }, [tab, loadEvents, loadSummary]);

  const exportEventsCsv = async () => {
    const apiUrl = process.env.REACT_APP_API_BASE_URL ?? '';
    const q = new URLSearchParams(qs()); if (kind) q.set('kind', kind); if (outcome) q.set('outcome', outcome);
    const res = await fetch(`${apiUrl}/api/v1/admin/payments/events.csv?${q.toString()}`, opts());
    if (!res.ok) { setError(`Export failed (${res.status})`); return; }
    const href = URL.createObjectURL(await res.blob());
    const a = document.createElement('a'); a.href = href; a.download = 'policyora-payment-events.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(href);
  };

  const submitRefund = async () => {
    if (!refunding) return;
    try {
      const r = await adminRefundNote(refunding.id, { reason: refundReason, ...(refundAmount ? { amount: refundAmount } : {}) });
      setNotice(`Refund noted — payment now ${r.payment_status}`); setRefunding(null); setRefundAmount(''); setRefundReason(''); load();
    } catch (e) { setError(e instanceof Error ? e.message : 'Refund note failed'); }
  };

  const outcomeChip = (o: string): React.CSSProperties => ({
    ...S.chip,
    background: o === 'ok' ? 'rgba(2,122,72,0.10)' : o === 'failed' ? 'rgba(240,68,56,0.10)' : o === 'unmatched' ? 'rgba(247,144,9,0.14)' : 'var(--ds-surface-2)',
    color: o === 'ok' ? 'var(--ds-success)' : o === 'failed' ? 'var(--ds-danger)' : o === 'unmatched' ? 'var(--ds-warning)' : 'var(--ds-text-secondary)',
  });
  const tile = (label: string, value: React.ReactNode, hint?: string) => (
    <div key={label} style={{ flex: '1 1 160px', padding: '10px 14px', border: '1px solid var(--ds-border)', borderRadius: 8, background: 'var(--ds-surface)' }}>
      <div style={{ ...S.muted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--ds-text)' }}>{value}</div>
      {hint && <div style={{ ...S.muted, fontSize: '0.78rem' }}>{hint}</div>}
    </div>
  );

  const exportCsv = async () => {
    const apiUrl = process.env.REACT_APP_API_BASE_URL ?? '';
    const res = await fetch(`${apiUrl}/api/v1/admin/invoices.csv?${qs()}`, opts());
    if (!res.ok) { setError(`Export failed (${res.status})`); return; }
    const href = URL.createObjectURL(await res.blob());
    const a = document.createElement('a'); a.href = href; a.download = 'policyora-payments.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(href);
  };

  const total = rows.filter((r) => r.status === 'completed').reduce((acc, r) => acc + Number(r.amount), 0);
  const statusChip = (s: string): React.CSSProperties => ({
    ...S.chip, background: s === 'completed' ? 'rgba(2,122,72,0.10)' : s === 'failed' ? 'rgba(240,68,56,0.10)' : s === 'refunded' ? 'rgba(247,144,9,0.14)' : 'var(--ds-surface-2)',
    color: s === 'completed' ? 'var(--ds-success)' : s === 'failed' ? 'var(--ds-danger)' : s === 'refunded' ? 'var(--ds-warning)' : 'var(--ds-text-secondary)',
  });

  return (
    <DashboardLayout collapsed={sidebarCollapsed} activeSection={activeSection} welcomeLabel="Invoices" selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed((p) => !p)} onNavClick={handleNavClick} onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout} onRegulationModalClose={() => setSelectedRegulation(null)}>
      <section style={S.section}>
        <h2 style={S.h2}>Invoices &amp; payments</h2>
        <p style={S.sub}>Every checkout, renewal, failed charge and refund recorded from Stripe and PayPal. Invoice numbers are issued for paid and refunded rows; the PDF is the PolicyOra invoice, the gateway receipt is the provider's own.</p>
        {error && <div style={S.error}>{error}</div>}
        {notice && <div style={S.ok}>{notice}</div>}
        <div style={S.tabs} role="tablist">
          {([['payments', 'Payments'], ['activity', 'Activity'], ['reconcile', 'Reconciliation']] as const).map(([k, label]) => (
            <button key={k} role="tab" aria-selected={tab === k} style={S.tab(tab === k)} onClick={() => setTab(k)}>{label}</button>
          ))}
        </div>
        <div style={S.toolbar}>
          <input style={S.input} placeholder="Customer email" value={email} onChange={(e) => setEmail(e.target.value)} />
          {tab === 'payments' && <select style={S.input} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Any status</option><option value="completed">Completed</option><option value="pending">Pending</option><option value="failed">Failed</option><option value="refunded">Refunded</option>
          </select>}
          {tab === 'activity' && <>
            <select style={S.input} aria-label="Event kind" value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">Any event</option>{Object.entries(kinds).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <select style={S.input} aria-label="Outcome" value={outcome} onChange={(e) => setOutcome(e.target.value)}>
              <option value="">Any outcome</option><option value="ok">Applied</option><option value="failed">Failed</option><option value="ignored">Ignored</option><option value="unmatched">Unmatched</option><option value="duplicate">Replayed</option>
            </select>
          </>}
          <input style={S.input} type="date" value={since} onChange={(e) => setSince(e.target.value)} title="From" />
          <input style={S.input} type="date" value={until} onChange={(e) => setUntil(e.target.value)} title="To" />
          <button style={S.btn} onClick={tab === 'payments' ? load : tab === 'activity' ? loadEvents : loadSummary} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
          {tab !== 'reconcile' && <button style={S.primary} onClick={tab === 'payments' ? exportCsv : exportEventsCsv}>Export CSV</button>}
          {tab === 'payments' && <span style={{ ...S.muted, marginLeft: 'auto', fontSize: '0.85rem' }}>{rows.length} rows · collected {fmtMoney(total, 'USD')}</span>}
        </div>
        {tab === 'reconcile' && summary && (
          <div data-testid="reconciliation">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
              {tile('Gross collected', fmtMoney(summary.gross_completed, 'USD'))}
              {tile('Refunded', fmtMoney(summary.refunded, 'USD'))}
              {tile('Net', fmtMoney(summary.net, 'USD'))}
              {tile('Declined attempts', summary.declined_attempts, 'checkout + renewal')}
              {Object.entries(summary.webhooks).map(([gw, w]) => tile(`${gw} webhooks`, w.last_received_at ? `${w.days_since} d ago` : 'never',
                `${w.unmatched} unmatched · ${w.duplicates} replayed`))}
            </div>
            <table style={S.table}>
              <thead><tr><th style={S.th}>Gateway</th><th style={S.th}>Status</th><th style={S.th}>Count</th><th style={S.th}>Total</th></tr></thead>
              <tbody>{Object.entries(summary.by_gateway).flatMap(([gw, st]) => Object.entries(st).map(([k, v]) => (
                <tr key={`${gw}-${k}`}><td style={S.td}>{gw}</td><td style={S.td}><span style={statusChip(k)}>{k}</span></td><td style={S.td}>{v.count}</td><td style={S.td}>{fmtMoney(v.total, 'USD')}</td></tr>)))}
              {Object.keys(summary.by_gateway).length === 0 && <tr><td style={S.td} colSpan={4}><span style={S.muted}>No payments in this range.</span></td></tr>}
              </tbody>
            </table>
          </div>
        )}
        {tab === 'activity' && (
          <div style={{ overflowX: 'auto', border: '1px solid var(--ds-border)', borderRadius: 8 }}>
            <table style={S.table}>
              <thead><tr><th style={S.th}>When</th><th style={S.th}>Customer</th><th style={S.th}>Event</th><th style={S.th}>Outcome</th><th style={S.th}>Amount</th><th style={S.th}>What happened</th><th style={S.th}>Reference</th></tr></thead>
              <tbody>
                {events.length === 0 && !loading && <tr><td style={S.td} colSpan={7}><span style={S.muted}>No activity matches.</span></td></tr>}
                {events.map((e) => (
                  <React.Fragment key={e.id}>
                    <tr data-testid={`event-${e.id}`} onClick={() => setOpen(open === e.id ? null : e.id)} style={{ cursor: e.details ? 'pointer' : 'default' }}>
                      <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{fmtWhen(e.created_at)}</td>
                      <td style={S.td}>{e.email || <span style={S.muted}>—</span>}</td>
                      <td style={S.td}>{e.kind_label}<div style={{ ...S.muted, fontSize: '0.72rem' }}>{e.gateway}{e.actor ? ` · by ${e.actor}` : ''}</div></td>
                      <td style={S.td}><span style={outcomeChip(e.outcome)}>{e.outcome === 'duplicate' ? 'replayed' : e.outcome === 'ok' ? 'applied' : e.outcome}</span></td>
                      <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{e.amount ? fmtMoney(e.amount, e.currency || 'USD') : '—'}</td>
                      <td style={S.td}>{e.summary}</td>
                      <td style={{ ...S.td, ...S.mono, fontSize: '0.72rem' }}>{e.gateway_ref || e.gateway_event_id || '—'}</td>
                    </tr>
                    {open === e.id && e.details && <tr><td style={S.td} colSpan={7}><pre style={{ ...S.mono, margin: 0, whiteSpace: 'pre-wrap', color: 'var(--ds-text-secondary)' }}>{JSON.stringify(e.details, null, 2)}</pre></td></tr>}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {refunding && (
          <div role="dialog" aria-label="Note a refund" style={{ padding: 14, border: '1px solid var(--ds-border-strong)', borderRadius: 8, marginBottom: 12, background: 'var(--ds-surface)' }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Note a refund for {refunding.email} — {fmtMoney(refunding.amount, refunding.currency)}</div>
            <p style={S.sub}>For money returned in the Stripe/PayPal dashboard or by bank transfer. Refunds made at the gateway with webhooks on are recorded automatically.</p>
            <div style={S.toolbar}>
              <input style={S.input} aria-label="Refund amount" placeholder={`Amount (blank = full ${refunding.amount})`} value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} />
              <input style={{ ...S.input, minWidth: 280 }} aria-label="Refund reason" placeholder="Reason (required)" value={refundReason} onChange={(e) => setRefundReason(e.target.value)} />
              <button style={S.primary} onClick={submitRefund} disabled={refundReason.trim().length < 3}>Record refund</button>
              <button style={S.btn} onClick={() => setRefunding(null)}>Cancel</button>
            </div>
          </div>
        )}
        {tab === 'payments' && <div style={{ overflowX: 'auto', border: '1px solid var(--ds-border)', borderRadius: 8 }}>
          <table style={S.table}>
            <thead><tr><th style={S.th}>Invoice</th><th style={S.th}>Date</th><th style={S.th}>Customer</th><th style={S.th}>Description</th><th style={S.th}>Gateway</th><th style={S.th}>Amount</th><th style={S.th}>Status</th><th style={S.th}></th></tr></thead>
            <tbody>
              {rows.length === 0 && !loading && <tr><td style={S.td} colSpan={8}><span style={S.muted}>No payments match.</span></td></tr>}
              {rows.map((r) => (
                <tr key={r.id}>
                  <td style={{ ...S.td, ...S.mono }}>{r.invoice_number || <span style={S.muted}>—</span>}</td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{fmtWhen(r.created_at)}</td>
                  <td style={S.td}>{r.email}{r.organisation && <div style={{ ...S.muted, fontSize: '0.78rem' }}>{r.organisation}</div>}</td>
                  <td style={S.td}>{r.description || '—'}</td>
                  <td style={S.td}>{r.gateway}{r.gateway_transaction_id && <div style={{ ...S.mono, ...S.muted, fontSize: '0.72rem' }}>{r.gateway_transaction_id}</div>}</td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap', fontWeight: 600 }}>{fmtMoney(r.amount, r.currency)}</td>
                  <td style={S.td}><span style={statusChip(r.status)}>{r.status}</span></td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                    {r.pdf_available && <button style={S.btn} onClick={() => downloadInvoicePdf(r.id, true).catch((e) => setError(e.message))}>PDF</button>}
                    {r.invoice_url && <a href={r.invoice_url} target="_blank" rel="noreferrer" style={{ marginLeft: 6, fontSize: '0.8rem' }}>receipt ↗</a>}
                    {r.status === 'completed' && <button style={{ ...S.btn, marginLeft: 6 }} onClick={() => { setRefunding(r); setNotice(null); }}>Refund…</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
      </section>
    </DashboardLayout>
  );
};

export default AdminInvoicesPage;
